import AbstractRow from "./AbstractRow.js";
import DimensionFormulaNode from "../../../keyFigure/formula/DimensionFormulaNode.js";
import LabelsDimensionSpecificationVisitor from "../../../dimensionSpecification/visitors/LabelsDimensionSpecificationVisitor.js";
import ShortTextRepresentationDimensionSpecificationVisitor from "../../../dimensionSpecification/visitors/ShortTextRepresentationDimensionSpecificationVisitor.js";
import SpecificationFactory from "../../../specification/specificationFactory.js";

/**
 * Abstract class for rows that have a calculated value.
 *
 * @abstract
 * @param {specification} [spec.dimensionSpecification] - Dimension specification if this is a dimension row.
 * @param {dimension} [spec.dimensionTree] - Root dimension from
 * which dimension ids are resolved. Mandatory if `dimensionSpecification`
 * is not empty.
 * @param {accountOperation} [spec.accountOperation] - The account operation of the row if any.
 * @param {boolean} [spec.visibleWhenNoData] - When true, the receiver's
 * visibility is not impacted if there is no corresponding data.
 */
export default class AbstractValueRow extends AbstractRow {
	constructor({
		dimensionSpecification,
		dimensionTree,
		accountOperation,
		visibleWhenNoData = false,
	} = {}) {
		super(...arguments);
		this._dimensionSpecification = dimensionSpecification;
		this._dimensionTree = dimensionTree;
		this._accountOperation = accountOperation;
		this._visibleWhenNoData = visibleWhenNoData;

		// True if at least one of the receiver's children rows has some non zero value.
		this._hasDataOrHasChildWithData = undefined;
	}

	getTitle() {
		return this._createTitleWithAccountOperationShortName(super.getTitle());
	}

	getDimensionSpecification() {
		if (this.hasOwnDimensionSpecification()) {
			return this._dimensionSpecification;
		}

		if (this._parent && this._parent.isRow()) {
			return this._parent.getDimensionSpecification();
		}

		return null;
	}

	getShortDimensionText() {
		if (!this._dimensionSpecification) {
			return "";
		}

		const visitor =
			new ShortTextRepresentationDimensionSpecificationVisitor({
				dimensionTree: this._dimensionTree,
			});
		return this._dimensionSpecification.accept(visitor);
	}

	getDimensionLabels() {
		if (!this._dimensionSpecification) {
			return [];
		}

		const visitor = new LabelsDimensionSpecificationVisitor({
			dimensionTree: this._dimensionTree,
		});
		return this._dimensionSpecification.accept(visitor);
	}

	getAccountOperation() {
		if (this._accountOperation) {
			return this._accountOperation;
		}

		if (this._parent && this._parent.isRow()) {
			return this._parent.getAccountOperation();
		}

		return null;
	}

	hasOwnDimensionSpecification() {
		return !!this._dimensionSpecification;
	}

	hasDimensionSpecification() {
		return !!this.getDimensionSpecification();
	}

	hasSettings() {
		return this.hasOwnDimensionSpecification();
	}

	/**
	 * Return true if the receiver has data for any column in the report
	 */
	hasData() {
		return this._report.hasDataForRow(this);
	}

	/**
	 * Return true if at least one of the receiver's children has value.
	 */
	hasDataOrHasChildWithData() {
		if (this._hasDataOrHasChildWithData !== undefined) {
			return this._hasDataOrHasChildWithData;
		}

		this._hasDataOrHasChildWithData =
			this.hasData() ||
			this.getRows().some((row) => row.hasDataOrHasChildWithData());

		return this._hasDataOrHasChildWithData;
	}

	/**
	 * @return {boolean} - Whether the row must only be displayed
	 * if the report contains data for it.
	 */
	isVisibleWhenNoData() {
		return this._visibleWhenNoData;
	}

	/**
	 * @return {number[]} The value of all cells in the receiver.
	 */
	getData() {
		return this.getReport().getDataForRow(this);
	}

	/**
	 * Recursively set `data` for the current row and `columns`.
	 * Cell values are also set in all sub-rows if there is data for them.
	 *
	 * Also see `abstractColumn.setDataForRow` for implementation details.
	 */
	setData({ data, columns }) {
		columns.forEach((column, index) => {
			const datum = data && data[index];
			column.setDataForRow({ row: this, data: datum });

			this._resetChildRowHasValue();

			if (datum && datum.children) {
				this._setColumnDataForChildren(datum.children, column);
			}
		});
	}

	asFormulaNode() {
		const node = super.asFormulaNode(...arguments);
		if (this.hasDimensionSpecification()) {
			return this._withDimensionFormulaNode(node);
		}

		return node;
	}

	accept(visitor) {
		return visitor.visitAbstractValueRow(this);
	}

	_resetChildRowHasValue() {
		this._hasDataOrHasChildWithData = undefined;
	}

	_setColumnDataForChildren(data, column) {
		this.getRows()
			.filter((row) => row.canBeCalculated())
			.forEach((child, index) => {
				child.setData({ columns: [column], data: [data[index]] });
			});
	}

	_withDimensionFormulaNode(formulaNode) {
		return new DimensionFormulaNode({
			expression: formulaNode,
			dimensionSpecification: this.getDimensionSpecification(),
		});
	}

	_accountOperationShortName() {
		const accountOperation = this.getAccountOperation();
		return accountOperation ? accountOperation.getShortName() : "";
	}

	_createTitleWithAccountOperationShortName(mainTitle) {
		const accountOperation = this.getAccountOperation();
		const accountOperationName =
			accountOperation && accountOperation.getShortName();

		if (accountOperationName) {
			return `${mainTitle} (${accountOperationName})`;
		}
		return mainTitle;
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		const dimensionSpecification = this.getDimensionSpecification();
		if (dimensionSpecification) {
			properties.dimensionSpecification = dimensionSpecification;
		}

		const accountOperation = this.getAccountOperation();
		if (accountOperation) {
			properties.accountOperation = accountOperation;
		}

		properties.visibleWhenNoData = this._visibleWhenNoData;

		return properties;
	}

	/**
	 * data.title can be a translatedString or a string.
	 * Avoiding this transformation will require a rework on the server side.
	 */
	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.dimensionSpecification &&= SpecificationFactory.materialize(
			data.dimensionSpecification,
			context,
		);

		data.dimensionTree = context.getDimensionTree();

		data.title =
			data.title && data.title.value ? data.title.value : data.title;
	}
}
